import dayjs from 'dayjs';
import { useMemo } from 'react';
import type { ExtendedInitiative, Initiative } from '../../../api/initiative-client/initiative-client.type';
import type { Goal, Outcome } from '../../../api/strategy-client/strategy-client.type';
import type { Epic } from '../../../api/tasks-client/task-client.type';
import type { StrategyEntityType } from '../strategy.type';

/**
 * Get initial form values based on entity type and existing entity
 */
export function useInitialValues(entity: Initiative | Outcome | Goal | undefined, _entityType: StrategyEntityType) {
  return useMemo(() => {
    if (!entity) {
      return {
        name: '',
        description: '',
        source: 'bloomfilter' as const,
        start_date: undefined,
        end_date: dayjs().add(1, 'year').toISOString(),
        relative_business_value: null,
        relative_business_value_unit: null,
        is_active: true,
      };
    }

    return {
      name: entity.name,
      description: entity.description,
      source: entity.source,
      start_date: entity.start_date,
      end_date: entity.end_date,
      relative_business_value: entity.relative_business_value,
      relative_business_value_unit: entity.relative_business_value_unit || null,
      is_active: entity.is_active,
    };
  }, [entity]);
}

/**
 * Get initial epics for an initiative
 */
export function useInitialEpics(
  entity: Initiative | Outcome | Goal | undefined,
  entityType: StrategyEntityType,
): Epic[] {
  return useMemo(() => {
    if (!entity || entityType !== 'initiative') {
      return [];
    }
    const initiative = entity as ExtendedInitiative;
    return initiative.epics || [];
  }, [entity, entityType]);
}

/**
 * Get initial initiatives for an outcome or goal
 */
export function useInitialInitiatives(
  entity: Initiative | Outcome | Goal | undefined,
  entityType: StrategyEntityType,
): Initiative[] {
  return useMemo(() => {
    if (!entity || (entityType !== 'outcome' && entityType !== 'goal')) {
      return [];
    }

    if (entityType === 'outcome') {
      const outcome = entity as Outcome;
      return outcome.initiatives || [];
    }

    const goal = entity as Goal;
    return goal.initiatives || [];
  }, [entity, entityType]);
}

/**
 * Get initial outcomes for a goal
 */
export function useInitialOutcomes(
  entity: Initiative | Outcome | Goal | undefined,
  entityType: StrategyEntityType,
): Outcome[] {
  return useMemo(() => {
    if (!entity || entityType !== 'goal') {
      return [];
    }
    const goal = entity as Goal;
    return goal.outcomes || [];
  }, [entity, entityType]);
}
